import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import SeoHead from '@/components/feature/SeoHead';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.discoveringmarche.com';

const SERVIZI_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Servizi Discovering Marche — Borghi storici Marche',
    description: 'Servizi di consulenza e accompagnamento nei borghi storici delle Marche: ricerca immobile, consulenza per clienti internazionali, assistenza completa in italiano, inglese e spagnolo.',
    url: `${SITE_URL}/servizi`,
    provider: {
      '@type': 'LocalBusiness',
      name: 'Discovering Marche',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Castelfidardo',
        addressRegion: 'AN',
        addressCountry: 'IT',
      },
    },
    areaServed: 'Marche, Italy',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Servizi', item: `${SITE_URL}/servizi` },
    ],
  },
];

const SERVIZI_DATA = [
  {
    key: '0',
    icon: 'ri-quill-pen-line',
    color: 'bg-stone-100 text-stone-500',
    steps: ['servizi.0.step1', 'servizi.0.step2', 'servizi.0.step3', 'servizi.0.step4'],
    image: 'https://readdy.ai/api/search-image?query=charming%20Italian%20medieval%20borgo%20stone%20village%20square%20local%20artisan%20winery%20agriturismo%20Marche%20countryside%20warm%20golden%20light%20authentic%20atmosphere&width=700&height=500&seq=serv000&orientation=landscape',
  },
  {
    key: '1',
    icon: 'ri-building-line',
    color: 'bg-terracotta-50 text-terracotta-300',
    steps: ['servizi.1.step1', 'servizi.1.step2', 'servizi.1.step3', 'servizi.1.step4'],
    image: 'https://readdy.ai/api/search-image?query=Italian%20historic%20borgo%20stone%20buildings%20medieval%20architecture%20Marche%20countryside%20beautiful%20rustic%20farmhouse%20restoration%20elegant&width=700&height=500&seq=serv001&orientation=landscape',
  },
  {
    key: '2',
    icon: 'ri-global-line',
    color: 'bg-olive-50 text-olive-600',
    steps: ['servizi.2.step1', 'servizi.2.step2', 'servizi.2.step3', 'servizi.2.step4'],
    image: 'https://readdy.ai/api/search-image?query=international%20real%20estate%20consultation%20meeting%20elegant%20office%20Italy%20professional%20multilingual%20service%20foreign%20buyers%20property&width=700&height=500&seq=serv002&orientation=landscape',
  },
];

interface ServizioCardProps {
  serv: typeof SERVIZI_DATA[0];
  idx: number;
}

const ServizioCard = ({ serv, idx }: ServizioCardProps) => {
  const { t } = useTranslation();
  const ref = useScrollAnimation({ threshold: 0.1 });
  const isEven = idx % 2 === 0;

  return (
    <div
      ref={ref.ref}
      className={`fade-in-up ${ref.isVisible ? 'visible' : ''} grid grid-cols-1 lg:grid-cols-2 gap-12 items-center`}
    >
      <div className={`${isEven ? 'lg:order-1' : 'lg:order-2'} rounded-2xl overflow-hidden aspect-[4/3]`}>
        <img src={serv.image} alt={t(`servizi.${serv.key}.title`)} className="w-full h-full object-cover object-top" />
      </div>
      <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
        <div className={`w-14 h-14 flex items-center justify-center rounded-2xl ${serv.color} mb-6`}>
          <i className={`${serv.icon} text-2xl`}></i>
        </div>
        <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-3">
          {t('servizi.overline')} {String(idx + 1).padStart(2, '0')}
        </p>
        <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-4 leading-snug">
          {t(`servizi.${serv.key}.title`)}
        </h2>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed mb-8">
          {t(`servizi.${serv.key}.desc`)}
        </p>
        <div className="space-y-3 mb-8">
          {serv.steps.map((stepKey, si) => (
            <div key={stepKey} className="flex items-start gap-4">
              <div className="w-7 h-7 flex items-center justify-center rounded-full bg-terracotta-50 text-terracotta-300 font-sans text-xs font-semibold flex-shrink-0 mt-0.5">
                {si + 1}
              </div>
              <p className="font-sans text-sm text-anthracite-600 leading-relaxed">{t(stepKey)}</p>
            </div>
          ))}
        </div>
        <Link
          to="/contatti"
          className="inline-flex items-center gap-2 font-sans text-sm tracking-widest uppercase text-terracotta-300 hover:text-terracotta-400 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('servizi.cta')}
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );
};

const ServiziPage = () => {
  const { t } = useTranslation();
  const header = useScrollAnimation();
  const timeline = useScrollAnimation({ threshold: 0.05 });

  return (
    <main className="min-h-screen bg-white">
      <SeoHead
        title="Servizi — Discovering Marche | Consulenza borghi storici Marche"
        description="Servizi di consulenza e accompagnamento nei borghi storici delle Marche. Ricerca immobile, assistenza per clienti internazionali in italiano, inglese e spagnolo con Discovering Marche."
        keywords="servizi Discovering Marche, consulenza borghi Marche, immobili borghi Marche, assistenza internazionale Marche"
        canonical="/servizi"
        jsonLd={SERVIZI_JSONLD}
      />
      <section className="relative h-[50vh] min-h-[380px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Marche%20Italy%20real%20estate%20services%20professional%20elegant%20consultation%20property%20buying%20selling%20borghi%20countryside%20beautiful%20landscape&width=1920&height=800&seq=serviziHero&orientation=landscape"
            alt="Servizi Marche Casa — immobili borghi Marche"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/10"></div>
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pb-14">
          <p className="font-sans text-xs tracking-widest uppercase text-terracotta-200 mb-4">{t('servizi.overline')}</p>
          <h1 className="font-serif text-4xl md:text-6xl text-white font-light max-w-2xl leading-tight">
            {t('servizi.page.title')}
          </h1>
        </div>
      </section>

      <section
        ref={header.ref}
        className={`fade-in-up ${header.isVisible ? 'visible' : ''} max-w-4xl mx-auto px-6 py-16 text-center`}
      >
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">{t('servizi.page.intro')}</p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-20 space-y-24">
        {SERVIZI_DATA.map((serv, idx) => (
          <ServizioCard key={serv.key} serv={serv} idx={idx} />
        ))}
      </section>

      <section className="bg-ivory-50 py-20 px-6">
        <div ref={timeline.ref} className={`fade-in-up ${timeline.isVisible ? 'visible' : ''} max-w-5xl mx-auto`}>
          <div className="text-center mb-14">
            <p className="font-sans text-xs tracking-widest uppercase text-terracotta-300 mb-4">{t('servizi.processo.overline')}</p>
            <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light">{t('servizi.processo.title')}</h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute top-8 left-0 right-0 h-px bg-ivory-300"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {[1, 2, 3, 4, 5].map((step) => (
                <div key={step} className="relative text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white border-2 border-terracotta-300 mx-auto mb-4 relative z-10">
                    <span className="font-serif text-xl text-terracotta-300">{step}</span>
                  </div>
                  <h3 className="font-serif text-base text-anthracite-700 mb-2">{t(`servizi.step${step}.title`)}</h3>
                  <p className="font-sans text-xs text-anthracite-400 leading-relaxed">{t(`servizi.step${step}.desc`)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-anthracite-800 font-light mb-6">{t('servizi.cta.title')}</h2>
          <p className="font-sans text-sm text-anthracite-500 mb-8 leading-relaxed">{t('servizi.cta.text')}</p>
          <Link
            to="/contatti"
            className="inline-block font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 rounded-full cursor-pointer whitespace-nowrap"
          >
            {t('servizi.cta.btn')}
          </Link>
        </div>
      </section>
    </main>
  );
};

export default ServiziPage;
